import React, { useCallback, useLayoutEffect, useMemo, useState } from "react";
import { RoutineEditScreenProps } from "../../RoutineEditMain/RoutineEditNavigation";
import { ScrollView } from "react-native";
import Animated, {
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";
import { Positions } from "../RoutineEditChecklistOrderSections";
import { notEmpty } from "../../../../utils";
import { TaskConnectionListItem } from "./TaskConnectionListItem";
import { useListContentStyle } from "../../../../components/mobile/useListContentStyle";
import { LIST_ITEM_HEIGHT } from "./utils";
import {
  useRoutineEditState,
  useRoutineEditStateSetParams,
} from "../../State/RoutineEditState";
import { useIntl } from "react-intl";
import { useNavigationHeader } from "@/src/common/navigationHeaderOptions";

export function RoutineEditChecklistSectionOrderTasks(
  props: RoutineEditScreenProps<"RoutineEditChecklistSectionOrderTasks">
) {
  const { sections = [], connections = [], tasks = [] } = useRoutineEditState();
  const setParams = useRoutineEditStateSetParams();

  const section = useMemo(
    () =>
      sections.find((_section) => _section.id === props.route.params.sectionId),
    [props.route.params.sectionId, sections]
  );

  const sectionConnections = useMemo(
    () => connections.filter(({ sectionId }) => section?.id === sectionId),
    [connections, section]
  );

  const connectedTasks = useMemo(
    () =>
      sectionConnections
        .map(
          (conn) =>
            [tasks.find((task) => task.id === conn.taskId), conn] as const
        )
        .sort(([_a, a], [_b, b]) => a.ordinal - b.ordinal),
    [sectionConnections, tasks]
  );

  const setOrder = useCallback(
    (orders: { [taskId: string]: number }) => {
      setParams({
        connections: connections.map((_connection) =>
          _connection.sectionId === section?.id
            ? { ..._connection, ordinal: orders[_connection.taskId] }
            : _connection
        ),
      });
    },
    [connections, section?.id, setParams]
  );

  const positions = useSharedValue<Positions>(
    Object.assign(
      {},
      ...connectedTasks
        .filter(([task]) => notEmpty(task))
        .map(([task], index) => ({ [task!.id]: index }))
    )
  );
  const [, setS] = useState(0);
  useLayoutEffect(() => {
    positions.value = Object.assign(
      {},
      ...connectedTasks
        .filter(([task]) => notEmpty(task))
        .map((task, index) => ({ [task[0]!.id]: index }))
    );
    setS((f) => f + 1);
  }, [connectedTasks, positions]);

  const height = useDerivedValue(() => {
    return Object.keys(positions.value).length * LIST_ITEM_HEIGHT;
  });
  const listStyle = useAnimatedStyle(() => ({
    height: withTiming(height.value),
  }));
  const contentContainerStyle = useListContentStyle();

  const intl = useIntl();

  useNavigationHeader({
    type: "DONE",
    title: intl.formatMessage({
      defaultMessage: "Reorder tasks",
      description: "RoutineEditChecklistSectionOrderTasks title",
    }),
    defaultContentStyle: true,
    onDone: () => {
      props.navigation.goBack();
    },
  });

  if (!section) {
    return null;
  }

  return (
    <ScrollView contentContainerStyle={contentContainerStyle}>
      <Animated.View style={listStyle}>
        {connectedTasks.map(([task, connection]) => (
          <TaskConnectionListItem
            key={task!.id}
            onReorder={(pos) => {
              setOrder(pos);
            }}
            listHeight={height}
            positions={positions}
            connection={connection}
            task={task!}
            isSingle={connectedTasks.length === 1}
          />
        ))}
      </Animated.View>
    </ScrollView>
  );
}
